import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Pagination,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import {
  getAllEmployeesByCreatedBy,
  registerUser,
  updateUser,
  getEmployeeMonthlySalaryReportAPI,
} from "../helpers/api.ts";
import { ToastContainer, toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Common/Breadcrumb.js";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("Add");
  const [formData, setFormData] = useState({
    EmployeeName: "",
    role: "",
    DateOfJoining: "",
    BasicSalary: "",
    Allowances: "",
    Deductions: "",
    email:"",
  });
  const [employeeId, setEmployeeId] = useState("");
  const [salaryMonth, setSalaryMonth] = useState(""); // To store selected month

  const logUser = JSON.parse(localStorage.getItem("user"));

  const handleModalClose = () => {
    setShowModal(false);
    resetFormData();
  };

  const resetFormData = () => {
    setFormData({
      EmployeeName: "",
      role: "",
      DateOfJoining: "",
      BasicSalary: "",
      Allowances: "",
      Deductions: "",
      email: "",
    });
  };

  const handleModalShow = (type, user = {}) => {
    setModalType(type);
    if (type === "Update") {
      setEmployeeId(user.EmployeeID);
      setFormData({
        EmployeeName: user.EmployeeName,
        role: user.role,
        DateOfJoining: user.DateOfJoining,
        BasicSalary: user.BasicSalary,
        Allowances: user.Allowances,
        Deductions: user.Deductions,
        email: user.email,
      });
    }
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        createdBy: logUser.id,
        updatedBy: logUser.id,
      };

      const response =
        modalType === "Add"
          ? await registerUser(payload)
          : await updateUser(employeeId, payload);

      if (response.success) {
        toast.success(
          modalType === "Add"
            ? "Employee added successfully"
            : "Employee updated successfully"
        );
      } else {
        toast.error(response.message || "Action failed");
      }
    } catch (error) {
      toast.error("An error occurred");
      console.error(error);
    } finally {
      handleModalClose();
      fetchUsers();
    }
  };

  const fetchUsers = async () => {
    try {
      const userData = {
        currentPage: currentPage,
        pageSize: pageSize,
        createdBy: logUser.id,
      };

      const response = await getAllEmployeesByCreatedBy(userData);

      setUsers(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      toast.error("An error occurred while fetching users");
      console.error(error);
    }
  };

  const handleLimitChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1); // Reset to first page on limit change
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDownloadSalaryReport = async () => {
    if (!salaryMonth) {
      toast.error("Please select a month and year first.");
      return;
    }

    try {
      const salaryData = await getEmployeeMonthlySalaryReportAPI({
        createdBy: logUser.id,
        month: salaryMonth,
      });

      // if (!salaryData.success){
      //   toast.error(salaryData.message || "Error generating salary report.");
      //   return;
      // }

      if (salaryData && salaryData.data && salaryData.data.length > 0) {
        const formattedData = salaryData.data.map((salary) => ({
          "Employee ID": salary.EmployeeID || "N/A",
          "Employee Name": salary.EmployeeName || "N/A",
          Month: salary.Month || "N/A",
          "Basic Salary": salary.BasicSalary || 0,
          Allowances: salary.Allowances || 0,
          Deductions: salary.Deductions || 0,
          "Advance Salary Given": salary.AdvanceSalaryGiven || 0,
          "Total Salary": salary.TotalSalary || 0,
          "Salary Paid": salary.SalaryPaid || 0,
          "Pending Salary": salary.PendingSalary || 0,
          "Leave Taken": salary.LeaveTaken || 0,
          "Leave Balance": salary.LeaveBalance || 0,
          "Leave Adjustment": salary.LeaveAdjustment || 0,
        }));

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employee Monthly Salary");

        const fileName = "Employee_Monthly_Salary_Report.csv";
        XLSX.writeFile(wb, fileName);

        toast.success("Salary report downloaded successfully!");
      } else {
        toast.error("No data available for the salary report.");
      }
    } catch (error) {
      toast.error("Error generating salary report.");
      console.error(error);
    } finally {
      handleModalClose();
    }
  };

  const handleMonthChange = (e) => {
    const selectedDate = e.target.value; // Get the selected month (YYYY-MM)
    setSalaryMonth(selectedDate); // Set the selected month
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize]);

  const rolesOptions = [
    { value: "RECEPTIONIST", label: "Receptionist" },
    { value: "MANAGER", label: "Manager" },
    { value: "HOUSEKEEPER", label: "Housekeeper" },
    { value: "SUPERVISOR", label: "Supervisor" },
    { value: "SPA", label: "Spa Staff" },
    { value: "ELECTRICIAN", label: "Electrician" },
    { value: "PLUMBER", label: "Plumber" },
    { value: "KITCHEN STAFF", label: "Kitchen Staff" },
    { value: "COOK", label: "Cook" },
    { value: "FOOD AND BEVERAGE", label: "Food and Beverage" },
    { value: "SECURITY", label: "Security" },
  ];

  return (
    <Container className="px-3 py-5 mb-5">
      <ToastContainer />
      <h6 className="mt-5">
        <Breadcrumbs title="Portal" breadcrumbItem="Users" />
      </h6>
      <div className="d-flex justify-content-end flex-column flex-md-row gap-2 mb-3">
        <Button
          variant="secondary"
          className="w-fit w-md-auto"
          onClick={() => navigate("/dashboard")}
        >
          Back
        </Button>
        <Button
          variant="primary"
          className="w-fit w-md-auto"
          onClick={() => handleModalShow("Add")}
        >
          Add Employee
        </Button>
        <Button
          variant="secondary"
          className="w-fit w-md-auto"
          onClick={() => handleModalShow("SalaryReport")}
        >
          Download Employee Salary Report
        </Button>
      </div>

      <div className="table-responsive">
      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th>#</th>
            <th>Employee Name</th>
            <th className="d-none d-md-table-cell">Employee Email</th>
            <th className="d-none d-lg-table-cell">Job Title</th>
            <th className="d-none d-lg-table-cell">Date Of Joining</th>
            <th className="d-none d-md-table-cell">Basic Salary</th>
            <th className="d-none d-lg-table-cell">Allowances</th>
            <th className="d-none d-lg-table-cell">Deductions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length > 0 ? (
            users.map((user, index) => (
              <tr key={user.EmployeeID}>
                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                <td>
                  <div className="d-flex flex-column">
                    <Link to={`/users/${user.EmployeeID}`}>
                      {user.EmployeeName}
                    </Link>
                    <small className="text-muted d-md-none">{user.role}</small>
                    <small className="text-muted d-md-none">{user.email}</small>
                  </div>
                </td>
                <td className="d-none d-md-table-cell">{user.email}</td>
                <td className="d-none d-md-flex d-lg-none">{user.role}</td>
                <td className="d-none d-lg-table-cell">{user.role}</td>
                <td className="d-none d-lg-table-cell">{new Date(user.DateOfJoining).toLocaleDateString()}</td>
                <td className="d-none d-md-table-cell">{user.BasicSalary}</td>
                <td className="d-none d-lg-table-cell">{user.Allowances}</td>
                <td className="d-none d-lg-table-cell">{user.Deductions}</td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    className="w-100 w-sm-auto"
                    onClick={() => handleModalShow("Update", user)}
                  >
                    Update
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center">
                No employees found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>
      {/* 
      <Pagination>
        {Array.from({ length: totalPages }, (_, index) => (
          <Pagination.Item
            key={index + 1}
            active={index + 1 === currentPage}
            onClick={() => handlePageChange(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination> */}

      <div className="d-flex justify-content-between align-items-center mt-3">
        <Form.Select
          aria-label="Select limit"
          value={pageSize}
          onChange={handleLimitChange}
          style={{ width: "150px" }}
        >
          <option value={10}>10</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={500}>500</option>
        </Form.Select>
        <Pagination className="mb-0">
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={i + 1 === currentPage}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
        </Pagination>
      </div>

      {/* Modal for adding employee */}
      {modalType === "Add" || modalType === "Update" ? (
        <Modal show={showModal} onHide={handleModalClose} className="modal-fullscreen-md-down">
          <Modal.Header closeButton>
            <Modal.Title>{modalType} Employee</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Employee Name</Form.Label>
                <Form.Control
                  type="text"
                  name="EmployeeName"
                  value={formData.EmployeeName}
                  onChange={handleFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Employee Email</Form.Label>
                <Form.Control
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Job Title</Form.Label>
                <Select
                  name="role"
                  value={{
                    label: formData.role,
                    value: formData.role,
                  }}
                  onChange={(selectedOption) => {
                    setFormData({
                      ...formData,
                      role: selectedOption.value,
                    });
                  }}
                  options={rolesOptions}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Date of Joining</Form.Label>
                <Form.Control
                  type="date"
                  name="DateOfJoining"
                  value={formData.DateOfJoining}
                  onChange={handleFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Basic Salary</Form.Label>
                <Form.Control
                  type="number"
                  name="BasicSalary"
                  value={formData.BasicSalary}
                  onChange={handleFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Allowances</Form.Label>
                <Form.Control
                  type="number"
                  name="Allowances"
                  value={formData.Allowances}
                  onChange={handleFormChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Deductions</Form.Label>
                <Form.Control
                  type="number"
                  name="Deductions"
                  value={formData.Deductions}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {modalType === "Add" ? "Add" : "Update"} Employee
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        // Modal for selecting salary month
        <Modal show={showModal} onHide={handleModalClose} className="modal-fullscreen-md-down">
          <Modal.Header closeButton>
            <Modal.Title>Select Month for Salary Report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Salary Month</Form.Label>
                <input
                  type="month"
                  value={salaryMonth}
                  onChange={handleMonthChange} // Handle month change
                  className="form-control"
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              Close
            </Button>
            <Button variant="primary" onClick={handleDownloadSalaryReport}>
              Download Report
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
};

export default Users;
